import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  listRegistryFleets,
  listRegistryVehicles,
  parseTokenClaims,
  RegistryFleet,
  RegistryVehicle,
  upsertRegistryVehicle,
} from "../api";

export function VehiclesPage() {
  const { t } = useTranslation(["dashboard", "common"]);
  const claims = parseTokenClaims();
  const myFirm = claims?.firm_id ?? "";
  const [vehicles, setVehicles] = useState<RegistryVehicle[]>([]);
  const [fleets, setFleets] = useState<RegistryFleet[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [id, setId] = useState("");
  const [fleetId, setFleetId] = useState("");
  const [plate, setPlate] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [vin, setVin] = useState("");

  async function refresh() {
    try {
      const [v, f] = await Promise.all([
        listRegistryVehicles(myFirm),
        listRegistryFleets(myFirm),
      ]);
      setVehicles(v);
      setFleets(f);
      setError(null);
    } catch (exc) {
      setError(String(exc));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onCreate(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await upsertRegistryVehicle({
        id,
        firm_id: myFirm,
        fleet_id: fleetId || null,
        plate: plate || null,
        make: make || null,
        model: model || null,
        vin: vin || null,
        active: true,
      });
      setId("");
      setPlate("");
      setMake("");
      setModel("");
      setVin("");
      refresh();
    } catch (exc) {
      setError(String(exc));
    }
  }

  return (
    <div className="page">
      <h2>{t("vehicles.title")}</h2>
      <p className="muted">{t("common:shell.firm", { id: myFirm })}</p>
      {error && <p className="error">{error}</p>}

      <form onSubmit={onCreate} className="row-form grid">
        <input placeholder={t("vehicles.idPlaceholder")} value={id}
               onChange={(e) => setId(e.target.value)} required />
        <select value={fleetId} onChange={(e) => setFleetId(e.target.value)}>
          <option value="">{t("vehicles.noFleet")}</option>
          {fleets.map((f) => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
        </select>
        <input placeholder={t("vehicles.plate")} value={plate} onChange={(e) => setPlate(e.target.value)} />
        <input placeholder={t("vehicles.make")} value={make} onChange={(e) => setMake(e.target.value)} />
        <input placeholder={t("vehicles.model")} value={model} onChange={(e) => setModel(e.target.value)} />
        <input placeholder={t("vehicles.vin")} value={vin} onChange={(e) => setVin(e.target.value)} />
        <button type="submit">{t("common:actions.createUpdate")}</button>
      </form>

      {loading ? (
        <p>{t("common:actions.loading")}</p>
      ) : vehicles.length === 0 ? (
        <p>{t("vehicles.empty")}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t("vehicles.th.id")}</th>
              <th>{t("vehicles.th.plate")}</th>
              <th>{t("vehicles.th.fleet")}</th>
              <th>{t("vehicles.th.makeModel")}</th>
              <th>{t("vehicles.th.vin")}</th>
              <th>{t("vehicles.th.active")}</th>
            </tr>
          </thead>
          <tbody>
            {vehicles.map((v) => (
              <tr key={v.id}>
                <td>{v.id}</td>
                <td>{v.plate ?? "-"}</td>
                <td>{v.fleet_id ?? "-"}</td>
                <td>{[v.make, v.model].filter(Boolean).join(" ") || "-"}</td>
                <td>{v.vin ?? "-"}</td>
                <td>{v.active ? t("common:bool.yes") : t("common:bool.no")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
